import { Bell, Code, List, MessageCircle, Phone } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'router-v6'

interface SetupItem {
  icon: ReactNode
  title: string
  // Mono eyebrow on the card, kind-of-thing not a repeat of the title.
  tag: string
  description: string
  to: string
}

interface SetupColumnData {
  heading: string
  subtitle: string
  items: SetupItem[]
}

const COLUMNS: SetupColumnData[] = [
  {
    heading: 'Build your first use case',
    subtitle: 'Seamlessly integrate your communication logic.',
    items: [
      {
        icon: <Phone className="size-3" />,
        title: 'SIP Trunking',
        tag: 'transport',
        description: 'Connect your SIP infrastructure',
        to: '/sip-trunking',
      },
      {
        icon: <Code className="size-3" />,
        title: 'Applications',
        tag: 'runtime',
        description: 'Create and manage applications',
        to: '/applications',
      },
    ],
  },
  {
    heading: 'Deploy',
    subtitle: 'Launch your services globally across multiple channels.',
    items: [
      {
        icon: <Phone className="size-3" />,
        title: 'Buy Phone Number',
        tag: 'pstn',
        description: 'Phone numbers for Voice and SMS',
        to: '/phone-numbers',
      },
      {
        icon: <MessageCircle className="size-3" />,
        title: 'Buy WhatsApp Number',
        tag: 'channel',
        description: 'WABA profiles, numbers and templates',
        to: '/whatsapp',
      },
    ],
  },
  {
    heading: 'Monitor Performance',
    subtitle: 'Track performance and gain realtime insights.',
    items: [
      {
        icon: <List className="size-3" />,
        title: 'Logs',
        tag: 'observability',
        description: 'View calls and messaging logs',
        to: '/logs',
      },
      {
        icon: <Bell className="size-3" />,
        title: 'Alerting',
        tag: 'notify',
        description: 'Configure alerts and notifications',
        to: '/alerting',
      },
    ],
  },
]

const SetupLinkItem = ({ icon, title, tag, description, to }: SetupItem) => (
  <Link
    to={to}
    data-testid={`setup-${to.slice(1)}-link`}
    className="group h-full rounded-md border border-st2 bg-background p-3 transition-colors hover:border-brand hover:bg-brand/5 focus-visible focus-visible:border-brand focus-visible:bg-brand/5"
  >
    <div className="flex flex-wrap items-center gap-2">
      {/* Bare glyph, no tinted container: 12px so the ink sits on the title's cap
          height (lucide draws phone/bell to the full box; 14px read 26% too tall),
          and one brand colour for every icon. brand-text (not brand) because
          this is an icon, which the kit lifts to #4d6aff on dark for contrast. */}
      <span className="shrink-0 text-brand-text" aria-hidden="true">
        {icon}
      </span>
      <span className="text-p-600 min-w-[120px] flex-1 break-words">{title}</span>
      <span className="font-jetbrains text-label uppercase text-secondary shrink-0">{tag}</span>
    </div>
    <p className="mt-2 text-xs-400 text-secondary">{description}</p>
  </Link>
)

const SetupColumn = ({ heading, subtitle, items, step }: SetupColumnData & { step: number }) => (
  <div className="flex flex-col gap-3 lg:row-span-3 lg:grid lg:grid-rows-[subgrid]">
    {/* mb-2 on the heading block only: the cards stay 12px apart while the
        column heading gets room to read as their parent. */}
    <div className="mb-2">
      <div className="flex items-baseline gap-2">
        <span className="font-jetbrains text-label uppercase text-secondary">
          {String(step).padStart(2, '0')}
        </span>
        {/* One step above the cards' own titles (text-p-600, 14px) so the
            column heading reads as their parent rather than a peer — but at
            weight 500, matching the page's own h2-500 "Welcome". A 600 here
            made the sub-heading heavier than the title above it. */}
        <h3 className="text-h3-500">{heading}</h3>
      </div>
      <p className="text-xs-400 text-secondary">{subtitle}</p>
    </div>
    {items.map((item) => (
      <SetupLinkItem key={item.title} {...item} />
    ))}
  </div>
)

export const SetupSection = () => (
  <section className="overflow-hidden rounded-lg border border-st2">
    <div className="flex h-10 items-center justify-between border-b border-st2 bg-background dark:bg-bg2 px-4">
      <h2 className="font-jetbrains text-s-400 text-secondary">
        <span className="text-tertiary">/</span>get-started
      </h2>
      <span className="flex items-center gap-1.5 font-jetbrains text-label uppercase text-secondary">
        <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
        setup
      </span>
    </div>
    <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-5 lg:grid-cols-3">
      {COLUMNS.map((column, i) => (
        <SetupColumn key={column.heading} step={i + 1} {...column} />
      ))}
    </div>
  </section>
)
